// Flur zeichnen (seit 30.10.2026 ohne KI, Betreiber: „Kundenauswahl übernehmen, Kunde fügt mit ein paar Griffen selbst
// die Türen im Grundriss hinzu; KI nur für Volumen und Möbel“). Erster Schritt nach dem Willkommen, vor den Flurfotos.
//
//   1. Form als Vorlage: Gerade, Um die Ecke, Mit Abzweig, Diele (Piktogramme).
//   2. Seit 31.10.2026 (Betreiber: „das ultimative Flurwerkzeug“) hat jeder Gang dieselben Möglichkeiten: am Ende
//      endet er, knickt links oder rechts ab oder beides (T); unterwegs Abzweige links, rechts oder als Kreuzung; von der
//      Diele gehen Gänge links, geradeaus oder rechts ab. Jeder neue Gang kann wieder dasselbe – so geht jede Form.
//      In der Skizze ziehbar: Wohnungstür (immer), Ecke ⤢ (Länge des Gangs), Abzweigstelle ↔ (Kreuzung wandert mit).
//      Alles wird in der Skizze bearbeitet (Betreiber: „immer visuell, keine Listen; Plus an der Skizze, Popup mit
//      Richtung“): „+“ je Gang öffnet ein Fenster mit Bild-Kacheln (Ecke, T, Abzweig, Kreuzung), die Nummer eines Gangs
//      ein Fenster zum Drehen oder Entfernen (samt allem, was an ihm hängt; Türen mit Fotos blockieren).
//   3. Türen: Finger auf eine Wand legen, kurz halten, ziehen, loslassen – erst dann rastet die Tür ein (ZiehSkizze).
//      Danach den Raum dahinter antippen. Antippen einer Tür: Raum ändern oder löschen; halten und ziehen: verschieben.
//
// Jede Tür ist sofort ein Raum (rooms.hall_room_id, hall_section, hall_side, hall_pos; hall_order aus der Lage). Keller,
// Dachboden und weitere Etagen wie bisher aufklappbar. Ältere Aufnahmen ohne hall_pos verteilen ihre Türen gleichmäßig.

import { useEffect, useState, type ReactNode } from "react";
import {
  FLOOR_MAX,
  FLOOR_MIN,
  HALL_SECTION_MAX,
  HALL_SHAPES,
  HALL_SHAPE_LABEL,
  ROOM_TYPES,
  ROOM_TYPE_LABEL,
  branchChildren,
  endChildren,
  floorLabel,
  normalizeSections,
  type HallSection,
  type HallShape,
  type HallSide,
  type RoomType,
} from "@umzugsscan/shared";
import { byFloorPlan, entranceHall, floorRank } from "../ablauf.ts";
import { useNav } from "../Erfassung.tsx";
import { doorKey, flurBild, laengeAus, parseWall, sectionRank, wallId } from "../flurGeometrie.ts";
import { useDraft, useStore } from "../hooks.ts";
import { newId, type DraftStore } from "../store.ts";
import { Button, Dialog, Footer } from "../ui/elemente.tsx";
import { ZiehSkizze, type Griff, type SkizzenTuer } from "../ui/ZiehSkizze.tsx";
import { roomName, type Room } from "../zustand.ts";

/** rooms.sort (0..1000) aus Etage und Reihenfolge, für Listen außerhalb des Grundrisses. */
const sortFor = (floor: number, order: number) => Math.min(1000, (floor >= 0 ? floor : 10 - floor) * 60 + Math.min(order, 59));

/** Namen je Raumtyp in Grundriss-Reihenfolge durchnummerieren ("Schlafzimmer", "Schlafzimmer 2"). */
export function tidyNames(store: DraftStore) {
  const rooms = store.getSnapshot().rooms;
  for (const type of ROOM_TYPES) {
    const group = rooms.filter((r) => r.type === type).sort(byFloorPlan);
    group.forEach((r, i) => {
      const name = roomName(type, group.length === 1 ? 1 : i + 1);
      if (r.name !== name) store.saveRoom({ ...r, name });
    });
  }
}

/** Räume, die fast jede Wohnung hat: zum Antippen. Alle anderen in der kleinen Liste. */
const HAEUFIG: RoomType[] = ["wohnen", "schlafen", "kueche", "bad", "kind", "arbeit", "abstellraum"];

/** Kleine Piktogramme für die Formfrage: Gang von unten (Eingang) nach oben. */
const PIKTO: Record<HallShape, string> = {
  gerade: "M20 36 V6",
  ecke: "M14 36 V12 H36",
  abzweig: "M20 36 V4 M20 18 H38",
  diele: "M6 8 H34 V34 H6 Z",
};
function Piktogramm(props: { form: HallShape }) {
  return (
    <svg viewBox="0 0 40 40" width="40" height="40" aria-hidden="true">
      <path d={PIKTO[props.form]} fill="none" stroke="currentColor" strokeWidth={props.form === "diele" ? 3 : 7} strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={props.form === "ecke" ? 14 : 20} cy="36" r="3" fill="currentColor" />
    </svg>
  );
}

/** Kacheln im Auswahlfenster: wie der Flur weitergeht, Gang von unten nach oben. */
const WEITER_PIKTO = {
  eckeLinks: "M24 44 V18 H6",
  eckeRechts: "M24 44 V18 H42",
  t: "M24 44 V18 M6 18 H42",
  abzweigLinks: "M24 44 V4 M24 24 H6",
  abzweigRechts: "M24 44 V4 M24 24 H42",
  kreuz: "M24 44 V4 M6 24 H42",
  geradeaus: "M24 44 V4",
} as const;

type Neu = { wall: string; pos: number };
/** Auswahlfenster an der Skizze: „+“ eines Gangs (nur = Vorlage Ecke/Abzweig) oder Nummer eines Gangs. */
type Menue = { art: "plus"; nr: number; nur?: "ecke" | "abzweig" } | { art: "gang"; nr: number } | { art: "weg"; nr: number };


export function Grundriss() {
  const d = useDraft();
  const store = useStore();
  const { weiter, zurueck } = useNav();
  const entrance = entranceHall(d.rooms);
  // Etagen (30.10.2026, Betreiber: „Obergeschoss muss wieder Skizze sein“): jede Etage mit eigenem Flur (flur, floor f,
  // hallOrder 0) und Türen; die Wohnungsebene nutzt den Flur am Eingang.
  const [etage, setEtage] = useState(0);
  const floorHallOf = (f: number) => (f === 0 ? entrance : (d.rooms.find((r) => r.type === "flur" && (r.floor ?? 0) === f && r.hallOrder === 0) ?? null));
  const gespeichert = floorHallOf(etage);
  const [platzhalter] = useState(() => new Map<number, string>());
  const [formWaehlen, setFormWaehlen] = useState(false);
  const [neu, setNeu] = useState<Neu | null>(null);
  const [auswahl, setAuswahl] = useState<string | null>(null);
  const [springen, setSpringen] = useState<number | null>(null);
  const [extraFloors, setExtraFloors] = useState<number[]>([]);
  const [remove, setRemove] = useState<Room | null>(null);
  const [mehr, setMehr] = useState(false);
  const [ohne, setOhne] = useState(false);
  const [tried, setTried] = useState(false);
  const [hinweis, setHinweis] = useState<string | null>(null);
  const [menue, setMenue] = useState<Menue | null>(null);

  // Den Flur der Eingangsebene gibt es immer; an ihm hängen die Türen.
  useEffect(() => {
    store.ensureEntranceHall();
  }, [store]);
  // Nach „+ Danach biegt der Flur noch einmal ab“: die neue Ecke oben zeigen (Betreiber 30.10.2026).
  useEffect(() => {
    if (springen == null) return;
    document.getElementById(`abschnitt-${springen}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
    setSpringen(null);
  }, [springen]);

  if (!entrance) return null;
  if (!platzhalter.has(etage)) platzhalter.set(etage, newId());
  // Flur einer weiteren Etage gibt es erst, wenn der Kunde seine Form wählt (dann gespeichert).
  const hall: Room = gespeichert ?? { id: platzhalter.get(etage)!, type: "flur", name: roomName("flur", 1), sort: sortFor(etage, 0), confirmedComplete: false, floor: etage, hallOrder: 0 };
  const hf = hall.floor ?? 0;

  const shape: HallShape | null = formWaehlen ? null : (hall.hallShape ?? null);
  const sections = normalizeSections(hall.hallSections);
  /** Kleinste freie Nummer für einen neuen Gang (höchstens HALL_SECTION_MAX). */
  const freeNr = (list: HallSection[]) => {
    for (let n = 2; n <= HALL_SECTION_MAX; n++) if (!list.some((s) => s.nr === n)) return n;
    return null;
  };
  const voll = freeNr(sections) === null;
  const doors = d.rooms.filter((r) => r.hallRoomId === hall.id);
  const bild = flurBild(sections, shape ?? "gerade");

  /** Lage einer Tür ohne gespeicherte Stelle (ältere Aufnahmen): gleichmäßig an ihrer Wand verteilt. */
  const posOf = (r: Room) => {
    if (r.hallPos != null) return r.hallPos;
    const same = doors.filter((x) => (x.hallSection ?? 1) === (r.hallSection ?? 1) && (x.hallSide ?? "ende") === (r.hallSide ?? "ende")).sort((a, b) => (a.hallOrder ?? 0) - (b.hallOrder ?? 0));
    return (same.indexOf(r) + 1) / (same.length + 1);
  };
  /** hall_order und sort aus der Lage neu setzen (Reihenfolge der Räume in der Fotoaufnahme). */
  const renumber = (list: Room[], secs: HallSection[]) => {
    const rank = sectionRank(secs);
    const sorted = [...list].sort((a, b) => doorKey(rank, a.hallSection, a.hallSide, a.hallPos) - doorKey(rank, b.hallSection, b.hallSide, b.hallPos));
    sorted.forEach((r, i) => {
      const order = i + 1;
      const next = { ...r, hallOrder: order, sort: sortFor(hf, order) };
      const now = store.getSnapshot().rooms.find((x) => x.id === r.id);
      if (!now || now.hallOrder !== order || now.sort !== next.sort || now.hallPos !== r.hallPos || now.hallSide !== r.hallSide || now.hallSection !== r.hallSection) store.saveRoom(next);
    });
    tidyNames(store);
  };
  const currentDoors = () => store.getSnapshot().rooms.filter((r) => r.hallRoomId === hall.id);

  const saveSections = (list: HallSection[], form: HallShape) => {
    store.saveRoom({ ...hall, hallSections: list, hallShape: form });
    // Türen an weggefallenen Abschnitten an Abschnitt 1 hängen, nichts geht verloren.
    const nrs = new Set(list.map((s) => s.nr));
    renumber(
      currentDoors().map((r) => (nrs.has(r.hallSection ?? 1) ? r : { ...r, hallSection: 1 })),
      list,
    );
  };
  /**
   * Die vier Formen sind Vorlagen (31.10.2026): Danach kann jeder Gang am Ende abknicken und unterwegs Abzweige haben.
   * Vorlagen mit Ecke/Abzweig fragen gleich nach der Richtung (richtung = null bis gewählt).
   */
  const chooseShape = (form: HallShape) => {
    setFormWaehlen(false);
    const start = { ...sections[0]!, kurz: false, laenge: null };
    if (hf === 0) dropLeftovers();
    saveSections([start], form);
    // Ecke und Abzweig: gleich im Fenster fragen, wohin (31.10.2026, Betreiber: „Popup mit Richtung“).
    if (form === "ecke" || form === "abzweig") setMenue({ art: "plus", nr: 1, nur: form });
  };
  /**
   * Räume aus „Unsere Wohnung hat keinen Flur“ (Raumliste), die ohne Tür und ohne Fotos übrig sind, wieder entfernen,
   * sobald der Kunde doch einen Flur zeichnet (Betreiber 31.10.2026: fremde Räume unter „Räume außerhalb der Wohnung“).
   */
  const dropLeftovers = () => {
    const draussen: RoomType[] = ["keller", "dachboden", "garage"];
    for (const r of d.rooms)
      if ((r.floor ?? 0) === 0 && r.id !== hall.id && !r.hallRoomId && !draussen.includes(r.type) && !d.photos.some((p) => p.roomId === r.id) && !d.rooms.some((x) => x.hallRoomId === r.id))
        store.deleteRoom(r.id);
  };
  /** Form aus den Abschnitten (für die Anzeige „Form: …“). */
  const formOf = (list: HallSection[]): HallShape => (shape === "diele" ? "diele" : list.some((s) => s.art === "abzweig") ? "abzweig" : list.length > 1 ? "ecke" : "gerade");
  const setSection = (nr: number, patch: Partial<HallSection>) => {
    const list = sections.map((s) => (s.nr === nr ? { ...s, ...patch } : s));
    saveSections(list, formOf(list));
  };
  /** Ein Gang und alles, was an ihm hängt. */
  const mitUnterbau = (nr: number, list = sections): number[] => [nr, ...list.filter((s) => s.von === nr && s.nr !== nr).flatMap((s) => mitUnterbau(s.nr, list))];
  /** Gänge entfernen; Türen ohne Fotos daran mit, Türen mit Fotos blockieren (erst verschieben). */
  const removeSections = (nrs: number[], list: HallSection[]): HallSection[] | null => {
    const weg = new Set(nrs.flatMap((n) => mitUnterbau(n, list)));
    const blocked = doors.filter((r) => weg.has(r.hallSection ?? 1) && d.photos.some((p) => p.roomId === r.id));
    if (blocked.length > 0) {
      setHinweis(`Zu ${blocked.map((r) => r.name).join(", ")} gibt es schon Fotos. Bitte die Tür erst an eine andere Stelle ziehen.`);
      return null;
    }
    for (const r of doors.filter((x) => weg.has(x.hallSection ?? 1))) store.deleteRoom(r.id);
    return list.filter((s) => !weg.has(s.nr));
  };
  const removeSection = (nr: number) => {
    const rest = removeSections([nr], sections);
    if (rest) saveSections(rest, formOf(rest));
  };
  /** „−“ an einem Gang: sofort weg, wenn nichts daran hängt; sonst kurz nachfragen (Türen, weitere Gänge). */
  const entfernen = (nr: number) => {
    const weg = new Set(mitUnterbau(nr));
    const haengt = doors.some((r) => weg.has(r.hallSection ?? 1)) || weg.size > 1;
    if (haengt) setMenue({ art: "weg", nr });
    else removeSection(nr);
  };
  /** Am Ende eines Gangs: endet, Ecke links, Ecke rechts oder beides (T). Bestehende Gänge bleiben erhalten. */
  const setEnde = (s: HallSection, want: ("links" | "rechts")[]) => {
    const ends = endChildren(sections, s.nr).filter((c) => c.richtung !== "geradeaus");
    const offen = ends.filter((c) => !c.richtung);
    const weg = ends.filter((c) => (c.richtung && !want.includes(c.richtung as "links" | "rechts")) || (!c.richtung && want.length === 0));
    let list = removeSections(weg.map((c) => c.nr), sections);
    if (!list) return;
    for (const w of want) {
      if (ends.some((c) => c.richtung === w)) continue;
      const frei = offen.find((c) => list!.some((x) => x.nr === c.nr && !x.richtung));
      if (frei) list = list.map((x) => (x.nr === frei.nr ? { ...x, richtung: w } : x));
      else {
        const nr = freeNr(list);
        if (nr === null) break;
        list = [...list, { nr, art: "ecke", richtung: w, von: s.nr }];
      }
    }
    saveSections(list, formOf(list));
  };
  /** Abzweig unterwegs anlegen; „beide“ = Kreuzung (zwei Abzweige an derselben Stelle). */
  const addAbzweig = (s: HallSection, seiten: ("links" | "rechts" | "geradeaus")[]) => {
    let list = sections;
    const bei = seiten.includes("geradeaus") ? 0.5 : branchChildren(sections, s.nr).length > 0 ? 0.75 : 0.5;
    for (const richtung of seiten) {
      const nr = freeNr(list);
      if (nr === null) break;
      list = [...list, { nr, art: "abzweig", richtung, von: s.nr, bei }];
    }
    saveSections(list, formOf(list));
    setSpringen(list.at(-1)!.nr);
  };
  /** Abzweigstelle ziehen: ein Gegenpart an derselben Stelle (Kreuzung) wandert mit. */
  const setBei = (nr: number, bei: number) => {
    const s = sections.find((x) => x.nr === nr);
    if (!s) return;
    const paar = sections.filter((x) => x.nr !== nr && x.von === s.von && x.art === "abzweig" && x.richtung !== s.richtung && Math.abs((x.bei ?? 0.5) - (s.bei ?? 0.5)) < 0.02);
    const list = sections.map((x) => (x.nr === nr || paar.includes(x) ? { ...x, bei } : x));
    saveSections(list, formOf(list));
  };

  // Türen in der Skizze.
  const ordered = [...doors].sort((a, b) => (a.hallOrder ?? 0) - (b.hallOrder ?? 0));
  const nrOf = (r: Room) => String(ordered.indexOf(r) + 1);
  const skizzenTueren: SkizzenTuer[] = [
    ...doors.map((r) => ({ key: r.id, wall: wallId(r.hallSection ?? 1, r.hallSide ?? "ende"), pos: posOf(r), nr: nrOf(r), label: r.name, aktiv: auswahl === r.id })),
    ...(neu ? [{ key: "neu", wall: neu.wall, pos: neu.pos, nr: "+", label: "?", aktiv: true }] : []),
  ];
  // Ziehbar (31.10.2026): die Wohnungstür immer (auch bei der Diele), jede Abzweigstelle ↔ und jede Ecke ⤢.
  const griffe: Griff[] = shape
    ? [
        { key: "eingang", wall: bild.eingangWaende.find((w) => w.id === `e:${bild.eingang.seite}`)!, pos: bild.eingang.pos, walls: bild.eingangWaende, label: "", art: "eingang" },
        ...sections.flatMap((s) => {
          const w = bild.abzweigWand.get(s.nr);
          // Kreuzung: nur ein Punkt für beide Seiten.
          const paar = sections.some((x) => x.nr < s.nr && x.von === s.von && x.art === "abzweig" && x.richtung !== s.richtung && Math.abs((x.bei ?? 0.5) - (s.bei ?? 0.5)) < 0.02);
          return w && !paar ? [{ key: `bei:${s.nr}`, wall: w, pos: Math.min(0.97, Math.max(0.03, s.bei ?? 0.5)), walls: [w], label: "↔", art: "punkt" as const }] : [];
        }),
        ...bild.laengen.map((g) => ({ key: `len:${g.nr}`, wall: g.wall, pos: g.pos, walls: [g.wall], label: "⤢", art: "punkt" as const })),
      ]
    : [];
  const onDrop = (key: string | null, wall: string, pos: number) => {
    if (key === "eingang") return setSection(1, { eingang: { seite: wall.slice(2) as "anfang" | "links" | "rechts", pos } });
    if (key?.startsWith("bei:")) return setBei(Number(key.slice(4)), pos);
    if (key?.startsWith("len:")) {
      const g = bild.laengen.find((x) => x.nr === Number(key.slice(4)));
      return g && setSection(g.nr, laengeAus(g, pos, g.nr === 1));
    }
    const { section, side } = parseWall(wall);
    if (key === null || key === "neu") {
      setNeu({ wall, pos });
      setAuswahl("neu");
      return;
    }
    renumber(
      currentDoors().map((r) => (r.id === key ? { ...r, hallSection: section, hallSide: side, hallPos: pos } : r)),
      sections,
    );
    setAuswahl(key);
  };
  const createRoom = (type: RoomType) => {
    if (!neu) return;
    const { section, side } = parseWall(neu.wall);
    const room: Room = {
      id: newId(),
      type,
      name: roomName(type, 1),
      sort: 50,
      confirmedComplete: false,
      floor: hf,
      hallRoomId: hall.id,
      hallSide: side,
      hallSection: section,
      hallPos: neu.pos,
      hallOrder: 99,
    };
    store.saveRoom(room);
    renumber([...currentDoors().filter((r) => r.id !== room.id), room], sections);
    setNeu(null);
    setAuswahl(null);
  };
  const changeType = (r: Room, type: RoomType) => {
    if (r.type !== type) store.saveRoom({ ...r, type });
    tidyNames(store);
    setAuswahl(null);
  };
  const deleteRoom = (r: Room) => {
    store.deleteRoom(r.id);
    setRemove(null);
    setAuswahl(null);
    renumber(currentDoors(), sections);
  };
  const askRemove = (r: Room) => (d.photos.some((p) => p.roomId === r.id) ? setRemove(r) : deleteRoom(r));

  // Keller, Dachboden, weitere Etagen.
  const floors = [...new Set([0, ...d.rooms.map((r) => r.floor ?? 0), ...extraFloors])].sort((a, b) => floorRank(a) - floorRank(b));
  const addFloor = (f: number) => {
    if (f >= FLOOR_MIN && f <= FLOOR_MAX && !floors.includes(f)) setExtraFloors([...extraFloors, f]);
  };
  const wechsel = (f: number) => {
    setEtage(f);
    setNeu(null);
    setAuswahl(null);
    setFormWaehlen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const neueEtage = (f: number) => {
    addFloor(f);
    wechsel(f);
  };
  const entferneEtage = (f: number) => {
    setExtraFloors(extraFloors.filter((x) => x !== f));
    wechsel(0);
  };
  const others = (f: number) => d.rooms.filter((r) => (r.floor ?? 0) === f && r.id !== hall.id && !r.hallRoomId).sort(byFloorPlan);
  const addOther = (type: RoomType, floor: number) => {
    const onFloor = d.rooms.filter((r) => (r.floor ?? 0) === floor);
    store.saveRoom({
      id: newId(),
      type,
      name: roomName(type, 1),
      sort: sortFor(floor, 50 + onFloor.length),
      confirmedComplete: false,
      floor,
      hallRoomId: null,
      hallOrder: floor === 0 ? null : Math.max(0, ...onFloor.map((r) => r.hallOrder ?? 0)) + 1,
    });
    tidyNames(store);
  };
  const withoutHall = () => {
    if (!d.photos.some((p) => p.roomId === entrance.id)) store.deleteRoom(entrance.id);
    else store.saveRoom({ ...entrance, hallOrder: null });
    store.go({ screen: "raeume", returnToCheck: false });
  };

  // Ältere Stände mit noch offener Richtung (Vorlagen bis 31.10.2026): Nummer antippen und Richtung wählen.
  const offen = sections.filter((s) => s.art !== "start" && !s.richtung);
  const roomCount = d.rooms.filter((r) => r.id !== hall.id).length;
  const error = !shape && hf === 0
    ? "Bitte wählen Sie die Form Ihres Flurs."
    : offen.length > 0
      ? "Bitte die Nummer des Gangs in der Skizze antippen und die Richtung wählen."
      : neu
        ? "Bitte wählen Sie den Raum hinter der neuen Tür."
        : roomCount === 0
          ? "Bitte ziehen Sie mindestens eine Tür an ihre Stelle."
          : null;
  const next = () => {
    setTried(true);
    if (error) return;
    tidyNames(store);
    weiter();
  };

  const selected = auswahl && auswahl !== "neu" ? doors.find((r) => r.id === auswahl) : null;
  /** Kachel im Auswahlfenster: Piktogramm (Gang von unten nach oben) und Text. */
  const kachel = (key: keyof typeof WEITER_PIKTO, label: string, onClick: () => void, opts: { on?: boolean; disabled?: boolean } = {}) => (
    <button
      key={key}
      type="button"
      className={`formknopf${opts.on ? " on" : ""}`}
      disabled={opts.disabled}
      onClick={() => {
        onClick();
        setMenue(null);
      }}
    >
      <svg viewBox="0 0 48 48" width="44" height="44" aria-hidden="true">
        <path d={WEITER_PIKTO[key]} fill="none" stroke="currentColor" strokeWidth="7" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      <span>{label}</span>
    </button>
  );

  /**
   * Auswahlfenster an der Skizze (31.10.2026, Betreiber: „alles visuell, Plus an der Skizze, Popup mit Richtung“):
   * „+“ eines Gangs → wie geht es weiter (Ecke, T, Abzweig, Kreuzung); Nummer eines Gangs → Richtung ändern oder entfernen.
   */
  const menueFenster = () => {
    if (!menue) return null;
    const s = sections.find((x) => x.nr === menue.nr);
    if (!s) return null;
    if (menue.art === "plus") {
      const endet = endChildren(sections, s.nr).filter((c) => c.richtung !== "geradeaus").length === 0;
      const ecken = menue.nur !== "abzweig" && endet;
      const abzweige = menue.nur !== "ecke" && !(s.art === "start" && s.kurz);
      return (
        <Dialog title={menue.nur === "ecke" ? "Wohin geht die Ecke?" : menue.nur === "abzweig" ? "Wohin geht der Abzweig?" : "Wie geht der Flur hier weiter?"} onClose={() => setMenue(null)} actions={<Button variant="quiet" onClick={() => setMenue(null)}>Abbrechen</Button>}>
          {ecken && (
            <div className="formwahl kacheln">
              {kachel("eckeLinks", "Ecke links", () => setEnde(s, ["links"]))}
              {kachel("eckeRechts", "Ecke rechts", () => setEnde(s, ["rechts"]))}
              {menue.nur !== "ecke" && kachel("t", "Links und rechts", () => setEnde(s, ["links", "rechts"]), { disabled: voll })}
            </div>
          )}
          {abzweige && (
            <>
              {ecken && <p className="hint">Oder geht unterwegs ein Gang ab, und geradeaus geht es weiter?</p>}
              <div className="formwahl kacheln">
                {kachel("abzweigLinks", "Abzweig links", () => addAbzweig(s, ["links"]), { disabled: voll })}
                {kachel("abzweigRechts", "Abzweig rechts", () => addAbzweig(s, ["rechts"]), { disabled: voll })}
                {kachel("kreuz", "Kreuzung", () => addAbzweig(s, ["links", "rechts"]), { disabled: voll })}
              </div>
            </>
          )}
          {voll && <p className="hint">Mehr Gänge gehen nicht – für die Skizze reicht das.</p>}
        </Dialog>
      );
    }
    if (menue.art === "weg") {
      const weg = new Set(mitUnterbau(s.nr));
      const tueren = doors.filter((r) => weg.has(r.hallSection ?? 1));
      return (
        <Dialog
          title={`Gang ${s.nr} entfernen?`}
          onClose={() => setMenue(null)}
          actions={
            <>
              <Button
                variant="danger"
                onClick={() => {
                  setMenue(null);
                  removeSection(s.nr);
                }}
              >
                Ja, entfernen
              </Button>
              <Button variant="quiet" onClick={() => setMenue(null)}>
                Abbrechen
              </Button>
            </>
          }
        >
          <p>
            Mit entfernt werden
            {weg.size > 1 ? ` ${weg.size - 1} weitere${weg.size === 2 ? "r Gang" : " Gänge"}, der daran hängt` : ""}
            {weg.size > 1 && tueren.length > 0 ? " und" : ""}
            {tueren.length > 0 ? ` die Türen zu ${tueren.map((r) => r.name).join(", ")}` : ""}.
          </p>
        </Dialog>
      );
    }
    // Gang antippen: Richtung ändern oder entfernen.
    const geschwister = sections.filter((x) => x.nr !== s.nr && x.von === s.von && x.art === s.art && (s.art === "ecke" || Math.abs((x.bei ?? 0.5) - (s.bei ?? 0.5)) < 0.02));
    const frei = (r: string) => !geschwister.some((x) => x.richtung === r);
    const richtungen = (shape === "diele" && s.von === 1 ? ["links", "geradeaus", "rechts"] : ["links", "rechts"]) as ("links" | "rechts" | "geradeaus")[];
    const pikto = (r: string) => (s.art === "ecke" ? (r === "links" ? "eckeLinks" : "eckeRechts") : r === "links" ? "abzweigLinks" : r === "rechts" ? "abzweigRechts" : "geradeaus");
    return (
      <Dialog
        title={`Gang ${s.nr}`}
        onClose={() => setMenue(null)}
        actions={
          <>
            <Button
              variant="danger"
              onClick={() => {
                setMenue(null);
                removeSection(s.nr);
              }}
            >
              Gang entfernen
            </Button>
            <Button variant="quiet" onClick={() => setMenue(null)}>
              Abbrechen
            </Button>
          </>
        }
      >
        <p className="hint">Geht der Gang in die andere Richtung? Antippen.</p>
        <div className="formwahl kacheln">
          {richtungen.map((r) =>
            kachel(pikto(r), r === "geradeaus" ? "Geradeaus" : r === "links" ? "Nach links" : "Nach rechts", () => setSection(s.nr, { richtung: r }), { on: s.richtung === r, disabled: s.richtung !== r && !frei(r) }),
          )}
        </div>
      </Dialog>
    );
  };

  return (
    <>
      <p className="eyebrow">Schritt 1 · Ihr Grundriss</p>
      <h1>{hf === 0 ? "Zeichnen Sie Ihren Flur" : `Flur im ${floorLabel(hf)}`}</h1>
      <p className="lead">
        {hf === 0
          ? "Stellen Sie sich an die Wohnungstür. So wissen wir, welche Räume es gibt und in welcher Reihenfolge Sie sie fotografieren."
          : "Stellen Sie sich oben an die Treppe. Gibt es hier keinen Flur, tragen Sie die Räume unten einzeln ein."}
      </p>
      <div className="etagen-reiter" role="tablist" aria-label="Etagen">
        {floors.map((f) => (
          <button key={f} type="button" role="tab" aria-selected={f === hf} className={`chip${f === hf ? " on" : ""}`} onClick={() => wechsel(f)}>
            {f === 0 ? "Wohnungsebene" : floorLabel(f)}
          </button>
        ))}
        {Math.max(...floors) < FLOOR_MAX && (
          <button type="button" className="chip chip-neu" onClick={() => neueEtage(Math.max(...floors) + 1)}>
            + Etage darüber
          </button>
        )}
        {Math.min(...floors) > FLOOR_MIN && (
          <button type="button" className="chip chip-neu" onClick={() => neueEtage(Math.min(...floors) - 1)}>
            + Keller
          </button>
        )}
      </div>

      {!shape ? (
        <section className="card">
          <h2>Wie sieht Ihr Flur aus?</h2>
          <div className="formwahl" role="group" aria-label="Form des Flurs">
            {HALL_SHAPES.map((f) => (
              <button key={f} type="button" className={`formknopf${hall.hallShape === f ? " on" : ""}`} onClick={() => chooseShape(f)}>
                <Piktogramm form={f} />
                <span>{HALL_SHAPE_LABEL[f]}</span>
              </button>
            ))}
          </div>
          {hf === 0 && (
            <Button variant="quiet" onClick={() => setOhne(true)}>
              Unsere Wohnung hat keinen Flur
            </Button>
          )}
        </section>
      ) : (
        <>
          <p className="form-gewaehlt">
            Form: <strong>{HALL_SHAPE_LABEL[shape]}</strong>{" "}
            <button type="button" className="link" onClick={() => setFormWaehlen(true)}>
              ändern
            </button>
          </p>

          <section className="card">
            <h2>Ihr Flur</h2>
            {/* Alles in der Skizze (31.10.2026, Betreiber): + für Ecken und Abzweige, Nummer antippen, Punkte ziehen. */}
            <ul className="skizze-legende">
              <li>
                <span className="lg-plus">+</span> Geht der Flur weiter? Antippen: Ecke, Abzweig oder Kreuzung.
              </li>
              <li>
                <strong>Tür eintragen:</strong> Finger auf eine Wand legen, kurz halten, an die Stelle ziehen, loslassen, Raum antippen.
              </li>
              <li>
                <strong>Verschieben:</strong> Wohnungstür (Pfeil){bild.laengen.length > 0 ? ", Ecke ⤢" : ""}
                {bild.abzweigWand.size > 0 ? ", Abzweig ↔" : ""} und Türen genauso ziehen. Gangnummer antippen: Richtung ändern. <span className="lg-minus">−</span> entfernt den Gang.
              </li>
            </ul>
            <div className="zieh-rahmen">
              <ZiehSkizze
                walls={bild.walls}
                doors={skizzenTueren}
                box={bild.box}
                label="Skizze des Flurs zum Eintragen der Türen"
                onDrop={onDrop}
                onTap={(k) => setAuswahl(auswahl === k ? null : k)}
                griffe={griffe}
                hindernisse={[
                  ...bild.heads.flatMap((h) => (h.nr === 1 ? [h.p] : [{ x: h.p.x - h.quer.x * 15, y: h.p.y - h.quer.y * 15 }, { x: h.p.x + h.quer.x * 15, y: h.p.y + h.quer.y * 15 }])),
                  ...(shape === "diele" ? bild.dielePlus.map((x) => x.p) : bild.plus.map((x) => x.p)),
                ]}
              >
                {/* Erst alle Gänge mit Rand, dann die Flächen ohne Rand darüber: keine Linien in Ecken und Abzweigen. */}
                {bild.rects.map((r, i) => (
                  <rect key={`r${i}`} x={r.x} y={r.y} width={r.w} height={r.h} className="skizze-gang" />
                ))}
                {bild.rects.map((r, i) => (
                  <rect key={`f${i}`} x={r.x + 1.5} y={r.y + 1.5} width={r.w - 3} height={r.h - 3} className="skizze-gang-innen" />
                ))}
                {bild.heads.map((h) => {
                  if (h.nr === 1)
                    return (
                      <text key={h.nr} x={h.p.x} y={h.p.y + 4} textAnchor="middle" className="skizze-abschnitt">
                        {h.nr}
                      </text>
                    );
                  // Nummer (antippen: drehen) und „−“ (Gang entfernen) quer zum Gang nebeneinander (31.10.2026).
                  const n = { x: h.p.x - h.quer.x * 15, y: h.p.y - h.quer.y * 15 };
                  const m = { x: h.p.x + h.quer.x * 15, y: h.p.y + h.quer.y * 15 };
                  return (
                    <g key={h.nr}>
                      <g className="skizze-gangnr" role="button" aria-label={`Gang ${h.nr}: Richtung ändern`} onClick={() => setMenue({ art: "gang", nr: h.nr })}>
                        <circle cx={n.x} cy={n.y} r="13" />
                        <text x={n.x} y={n.y + 4.5} textAnchor="middle">
                          {h.nr}
                        </text>
                      </g>
                      <g className="skizze-minus" role="button" aria-label={`Gang ${h.nr} entfernen`} onClick={() => entfernen(h.nr)}>
                        <circle cx={m.x} cy={m.y} r="13" />
                        <text x={m.x} y={m.y + 6} textAnchor="middle">
                          −
                        </text>
                      </g>
                    </g>
                  );
                })}
                {shape !== "diele" &&
                  bild.plus.map((pl) => (
                    <g key={`p${pl.nr}`} className="skizze-plus" role="button" aria-label={`Gang ${pl.nr}: wie geht es weiter?`} onClick={() => setMenue({ art: "plus", nr: pl.nr })}>
                      <circle cx={pl.p.x} cy={pl.p.y} r="15" />
                      <text x={pl.p.x} y={pl.p.y + 6.5} textAnchor="middle">
                        +
                      </text>
                    </g>
                  ))}
                {shape === "diele" &&
                  !voll &&
                  bild.dielePlus.map((pl) => (
                    <g key={pl.richtung} className="skizze-plus" role="button" aria-label={`Gang aus der Diele ${pl.richtung}`} onClick={() => addAbzweig(sections[0]!, [pl.richtung])}>
                      <circle cx={pl.p.x} cy={pl.p.y} r="15" />
                      <text x={pl.p.x} y={pl.p.y + 6.5} textAnchor="middle">
                        +
                      </text>
                    </g>
                  ))}
                <EingangsPfeil p={bild.entrance} d={bild.entranceDir} />
                <text
                  x={bild.entrance.x - bild.entranceDir.x * 40}
                  y={bild.entrance.y - bild.entranceDir.y * 48 + (bild.entranceDir.y === 0 ? -22 : 5)}
                  textAnchor={bild.entranceDir.x > 0 ? "end" : bild.entranceDir.x < 0 ? "start" : "middle"}
                  className="skizze-eingang-text"
                >
                  {hf === 0 ? "Wohnungstür" : "Treppe"}
                </text>
              </ZiehSkizze>
            </div>
            {doors.length === 0 && !neu && <p className="hint">Noch keine Tür. Halten Sie den Finger auf eine Wand der Skizze.</p>}

            {(neu || selected) && (
              <div className="neue-tuer card">
                <h3>{neu ? "Neue Tür: Welcher Raum ist dahinter?" : `Tür ${nrOf(selected!)}: ${selected!.name}`}</h3>
                <RaumChips current={selected?.type ?? null} onPick={(t) => (neu ? createRoom(t) : changeType(selected!, t))} />
                <div className="aktionen-zeile">
                  {selected && (
                    <Button variant="quiet" onClick={() => askRemove(selected)}>
                      Tür löschen
                    </Button>
                  )}
                  <Button
                    variant="quiet"
                    onClick={() => {
                      setNeu(null);
                      setAuswahl(null);
                    }}
                  >
                    {neu ? "Abbrechen" : "Fertig"}
                  </Button>
                </div>
              </div>
            )}
          </section>

        </>
      )}
      <Etage
        f={hf}
        zeigen={hf !== 0 || mehr || others(0).length > 0}
        titel={null}
        rooms={others(hf)}
        onOpen={() => setMehr(true)}
        onAdd={(t) => addOther(t, hf)}
        onRemove={askRemove}
        onRemoveFloor={hf !== 0 && others(hf).length === 0 && !gespeichert ? () => entferneEtage(hf) : undefined}
      />

      {menueFenster()}
      {tried && error && <p className="error">{error}</p>}
      <Footer onBack={zurueck} onNext={next} nextLabel="Weiter zu den Flurfotos" />

      {remove && (
        <Dialog
          title={`„${remove.name}“ entfernen?`}
          onClose={() => setRemove(null)}
          actions={
            <>
              <Button variant="danger" onClick={() => deleteRoom(remove)}>
                Ja, entfernen
              </Button>
              <Button variant="quiet" onClick={() => setRemove(null)}>
                Abbrechen
              </Button>
            </>
          }
        >
          <p>Für diesen Raum gibt es schon Fotos. Diese werden mit gelöscht.</p>
        </Dialog>
      )}
      {hinweis && (
        <Dialog title="Geht noch nicht" onClose={() => setHinweis(null)} actions={<Button onClick={() => setHinweis(null)}>OK</Button>}>
          <p>{hinweis}</p>
        </Dialog>
      )}
      {ohne && (
        <Dialog
          title="Wohnung ohne Flur"
          onClose={() => setOhne(false)}
          actions={
            <>
              <Button onClick={withoutHall}>Räume einzeln auswählen</Button>
              <Button variant="quiet" onClick={() => setOhne(false)}>
                Abbrechen
              </Button>
            </>
          }
        >
          <p>Dann wählen Sie Ihre Räume aus einer Liste und fotografieren sie nacheinander.</p>
        </Dialog>
      )}
    </>
  );
}

/** Raum hinter einer Tür: häufige Räume zum Antippen, seltene in der Liste. */
/** Pfeil der Wohnungstür: außen vor der Wand, zeigt in den Flur. */
function EingangsPfeil(props: { p: { x: number; y: number }; d: { x: number; y: number } }) {
  const { p, d } = props;
  const a = { x: p.x - d.x * 30, y: p.y - d.y * 30 };
  const n = { x: -d.y, y: d.x };
  const tip = { x: p.x - d.x * 2, y: p.y - d.y * 2 };
  return (
    <path
      d={`M${a.x} ${a.y} L${tip.x} ${tip.y} M${tip.x - d.x * 8 + n.x * 6} ${tip.y - d.y * 8 + n.y * 6} L${tip.x} ${tip.y} L${tip.x - d.x * 8 - n.x * 6} ${tip.y - d.y * 8 - n.y * 6}`}
      className="skizze-eingang"
    />
  );
}

export function RaumChips(props: { current: RoomType | null; onPick: (t: RoomType) => void; typen?: RoomType[] }) {
  const chips = props.typen ?? HAEUFIG;
  const selten = ROOM_TYPES.filter((t) => !chips.includes(t) && t !== "flur");
  return (
    <>
      <div className="chips">
        {chips.map((t) => (
          <button key={t} type="button" className={`chip${props.current === t ? " on" : ""}`} aria-pressed={props.current === t} onClick={() => props.onPick(t)}>
            {ROOM_TYPE_LABEL[t]}
          </button>
        ))}
      </div>
      <div className="tuer-raum-rest">
        <select aria-label="Anderer Raum" value={props.current && selten.includes(props.current) ? props.current : ""} onChange={(e) => e.target.value && props.onPick(e.target.value as RoomType)}>
          <option value="">Anderer Raum …</option>
          {selten.map((t) => (
            <option key={t} value={t}>
              {ROOM_TYPE_LABEL[t]}
            </option>
          ))}
        </select>
      </div>
    </>
  );
}

function Etage(props: {
  f: number;
  zeigen: boolean;
  titel: string | null;
  rooms: Room[];
  onOpen: () => void;
  onAdd: (t: RoomType) => void;
  onRemove: (r: Room) => void;
  onRemoveFloor?: () => void;
}) {
  if (!props.zeigen)
    return (
      <button type="button" className="aufklappen" aria-expanded={false} onClick={props.onOpen}>
        ▸ Keller, Dachboden, Garage oder weitere Etagen?
      </button>
    );
  return (
    <section className="card">
      {props.titel ? <h2>{props.titel}</h2> : <h3>{props.f === 0 ? "Räume außerhalb der Wohnung" : "Räume ohne Flurtür auf dieser Etage"}</h3>}
      {props.f === 0 && <p className="hint">Keller, Dachboden oder Garage. Balkon und Räume hinter einem Zimmer tragen Sie später im Zimmer an seiner Tür ein.</p>}
      {props.rooms.map((r) => (
        <div className="raum-zeile" key={r.id}>
          <span>{r.name}</span>
          <button type="button" className="raum-weg" aria-label={`${r.name} entfernen`} onClick={() => props.onRemove(r)}>
            ×
          </button>
        </div>
      ))}
      <RaumNeu onAdd={props.onAdd} />
      {props.onRemoveFloor && (
        <Button variant="quiet" onClick={props.onRemoveFloor}>
          Etage entfernen
        </Button>
      )}
    </section>
  );
}

function RaumNeu(props: { onAdd: (t: RoomType) => void; children?: ReactNode }) {
  const [type, setType] = useState<RoomType | "">("");
  return (
    <div className="raum-neu">
      <div className="field compact">
        <select aria-label="Raum hinzufügen" value={type} onChange={(e) => setType(e.target.value as RoomType | "")}>
          <option value="">Raum hinzufügen …</option>
          {ROOM_TYPES.filter((t) => t !== "flur").map((t) => (
            <option key={t} value={t}>
              {ROOM_TYPE_LABEL[t]}
            </option>
          ))}
        </select>
      </div>
      <Button
        variant="secondary"
        disabled={type === ""}
        onClick={() => {
          if (type === "") return;
          props.onAdd(type);
          setType("");
        }}
      >
        Hinzufügen
      </Button>
    </div>
  );
}

export type { HallSide };
